import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { WorkspaceConfig } from "@shared/types";
import { CrossfadePlayer } from "@/components/CrossfadePlayer";
import { ClockOverlay } from "@/components/ClockOverlay";
import { Button } from "@/components/ui/button";

interface PreviewOverlayProps {
  config: WorkspaceConfig;
  onClose: () => void;
}

/** Full-screen, real-browser-fullscreen preview of the playlist for the admin. */
export function PreviewOverlay({ config, onClose }: PreviewOverlayProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    el?.requestFullscreen?.().catch(() => undefined);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onFsChange = () => {
      if (!document.fullscreenElement) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFsChange);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
  }, [onClose]);

  return (
    <div ref={ref} role="dialog" aria-modal="true" aria-label="Preview" className="fixed inset-0 z-50 bg-black">
      <CrossfadePlayer config={config} muted={false} className="absolute inset-0" />
      <ClockOverlay config={config.settings.clockOverlay} />
      <Button
        variant="secondary"
        size="icon"
        className="absolute right-4 top-4 z-10 opacity-70 hover:opacity-100"
        onClick={onClose}
        aria-label="Close preview"
      >
        <X className="h-5 w-5" />
      </Button>
    </div>
  );
}
